import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Sparkles, 
  Target, 
  TrendingUp, 
  Shield, 
  Check, 
  ArrowRight,
  Users,
  Award,
  Zap,
  ChevronRight,
  Star,
  Database,
  BarChart3,
  FileText,
  MessageSquare,
  LinkIcon
} from 'lucide-react';
import LatestBlogs from './LatestBlogs';
import './LandingPage.css';
import './LandingPage-Modern.css';

const LandingPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleGetStarted = () => {
    navigate('/optimize');
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="landing-page">
      {/* Hero Section - Modern Design */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="hero-gradient-orb hero-orb-1"></div>
          <div className="hero-gradient-orb hero-orb-2"></div>
        </div>
        
        <div className="container">
          <div className="hero-grid">
            <motion.div 
              className="hero-left"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div 
                className="hero-badge-modern"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <Sparkles size={16} />
                <span>The #1 AI Tool For Optimizing Linkedin Profile</span>
              </motion.div>
              
              <motion.h1 
                className="hero-title-modern"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                AI-Powered LinkedIn Profile Optimizer for{' '}
                <span className="gradient-text">Founders, Consultants, Executives</span>{' '}
                and{' '}
                <span className="gradient-text">Job Seekers</span>
              </motion.h1>
              
              <motion.p 
                className="hero-subtitle-modern"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                Transform your LinkedIn profile into a high-ranking, high-converting digital asset. Improve visibility, credibility and opportunities with an advanced AI LinkedIn audit trusted by professionals across industries.
              </motion.p>
              
              <motion.div 
                className="hero-cta-modern"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                <button className="btn-primary-modern" onClick={handleGetStarted}>
                  <span>Optimize My Profile</span>
                  <ArrowRight size={20} />
                </button>
                <button
                  className="btn-secondary-modern"
                  onClick={() => { window.location.href = '/sample-audits'; }}
                >
                  <span>View Sample Audit</span>
                </button>
              </motion.div>
            </motion.div>
            
            <motion.div 
              className="hero-right"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="hero-stats-cards">
                <motion.div 
                  className="stat-card"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                >
                  <div className="stat-card-icon">
                    <Users size={24} />
                  </div>
                  <div className="stat-card-content">
                    <div className="stat-card-value">1000+</div>
                    <div className="stat-card-label">Profiles Optimized</div>
                  </div>
                </motion.div>
                
                <motion.div 
                  className="stat-card"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.7 }}
                >
                  <div className="stat-card-icon">
                    <TrendingUp size={24} />
                  </div>
                  <div className="stat-card-content">
                    <div className="stat-card-value">40%</div>
                    <div className="stat-card-label">increase in profile visits</div>
                  </div>
                </motion.div>
                
                <motion.div 
                  className="stat-card"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.8 }}
                >
                  <div className="stat-card-icon">
                    <Zap size={24} />
                  </div>
                  <div className="stat-card-content">
                    <div className="stat-card-value">7x</div>
                    <div className="stat-card-label">more post impressions</div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Modern Single Row */}
      <section id="testimonials" className="testimonials-section-modern">
        <div className="container">
          <motion.div 
            className="section-header-modern"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>What Users Say After Optimizing Their LinkedIn Profile</h2>
          </motion.div>
          
          <div className="testimonials-grid-modern">
            {[
              {
                quote: "The changes to my profile felt small, but they added up. Over the next month, I booked 10 sales calls from people who said they found me through LinkedIn.",
                author: "Jainam Shah",
                role: "Copy Writer and Marketer",
                rating: 5,
                image: "https://i.ibb.co/FLqQ92zh/1744096925971.jpg"
              },
              {
                quote: "I started getting more recruiter messages within days after optimizing my profile. The suggestions were on point and made my profile look far more professional.",
                author: "Aisha Verma",
                role: "Marketing Associate",
                rating: 5,
                image: "https://iili.io/fIlTopn.png"
              },
              {
                quote: "The optimizer helped me fix things I didn’t even know were hurting my visibility. Within a week, I saw a noticeable jump in profile views and connection requests.",
                author: "Rohit Sharma",
                role: "Product Analyst",
                rating: 5,
                image: "https://i.ibb.co/kV20ssdz/dbc582cb4a5ef9df9fc7e82e283947b2.jpg"
              }
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                className="testimonial-card-modern"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
              >
                <div className="testimonial-quote-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <path d="M9.135 9.46c1.205-.356 2.14-1.135 2.805-2.34.665-1.204.998-2.554.998-4.05h-4.5c0 1.498-.333 2.715-1 3.652-.666.938-1.61 1.407-2.832 1.407v4.5c1.498 0 2.83.356 3.998 1.069 1.168.712 2.005 1.765 2.531 3.158h4.5c0-1.496-.333-2.846-.998-4.05-.665-1.205-1.6-1.984-2.805-2.34L9.135 9.46zM21.135 9.46c1.205-.356 2.14-1.135 2.805-2.34.665-1.204.998-2.554.998-4.05h-4.5c0 1.498-.333 2.715-1 3.652-.666.938-1.61 1.407-2.832 1.407v4.5c1.498 0 2.83.356 3.998 1.069 1.168.712 2.005 1.765 2.531 3.158h4.5c0-1.496-.333-2.846-.998-4.05-.665-1.205-1.6-1.984-2.805-2.34l-1.697.996z" fill="#60C5FE" opacity="0.2"/>
                  </svg>
                </div>
                <div className="testimonial-stars-modern">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={18} fill="#60C5FE" color="#60C5FE" />
                  ))}
                </div>
                <p className="testimonial-quote-modern">"{testimonial.quote}"</p>
                <div className="testimonial-author-modern">
                  {testimonial.image ? (
                    <img 
                      src={testimonial.image} 
                      alt={testimonial.author}
                      className="author-avatar-modern author-avatar-image"
                    />
                  ) : (
                    <div className="author-avatar-modern">
                      {testimonial.author.charAt(0)}
                    </div>
                  )}
                  <div className="author-info-modern">
                    <div className="author-name-modern">{testimonial.author}</div>
                    <div className="author-role-modern">{testimonial.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Use Section - Modern Grid Design */}
      <section id="how-to-use" className="how-section-modern">
        <div className="container">
          <motion.div 
            className="section-header-modern"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>How to Use the LinkedIn Profile Optimizer?</h2>
          </motion.div>
          
          <div className="how-grid-modern">
            {[
              {
                icon: <LinkIcon size={28} />,
                step: "01",
                title: "Paste your LinkedIn profile URL",
                description: "We fetch data from your public profile and analyze all your profile sections.",
              },
              {
                icon: <MessageSquare size={28} />,
                step: "02",
                title: "Answer a few personalized questions",
                description: "Tell us about your goals and target audience for personalized recommendations.",
              },
              {
                icon: <Database size={28} />,
                step: "03",
                title: "AI analyzes all your profile sections",
                description: "Our AI study every section to identify strengths and improvement opportunities..",
              },
              {
                icon: <FileText size={28} />,
                step: "04",
                title: "Get a detailed report with score + strategy",
                description: "Unlock personalized actionable suggestions with optimization strategy, examples, and ready to paste content.",
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="how-card-modern"
                initial={{ opacity: 0.4, scale: 0.96, filter: "grayscale(0.3)" }}
                whileInView={{ opacity: 1, scale: 1, filter: "grayscale(0)" }}
                viewport={{ once: false, margin: "-120px", amount: 0.3 }}
                transition={{ 
                  duration: 0.7,
                  delay: index * 0.1,
                  ease: [0.34, 1.56, 0.64, 1]
                }}
                whileHover={{ scale: 1.03, transition: { duration: 0.2 } }}
              >
                <div className="how-card-header">
                  <div className="how-card-icon-wrapper">
                    {item.icon}
                  </div>
                  <div className="how-card-step">{item.step}</div>
                </div>
                <div className="how-card-body">
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
                <div className="how-card-arrow">
                  {index < 3 && <ArrowRight size={20} />}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Optimize Section */}
      <section id="why-optimize" className="why-section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Why You Need to Optimize Your LinkedIn Profile?</h2>
          </motion.div>
          
          <div className="why-grid">
            {[
              {
                icon: <Shield size={32} />,
                title: "Authority Building",
                description: "Position yourself as an expert with structured, clear storytelling.",
                color: "#60C5FE"
              },
              {
                icon: <Target size={32} />,
                title: "Lead Conversion",
                description: "Turn profile visitors into real leads and opportunities.",
                color: "#60C5FE"
              },
              {
                icon: <TrendingUp size={32} />,
                title: "Visibility Boost",
                description: "Rank higher in searches with better keywords and structure.",
                color: "#60C5FE"
              },
              {
                icon: <Star size={32} />,
                title: "Strong First Impression",
                description: "Build trust instantly with a clean, optimized profile.",
                color: "#60C5FE"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="why-card"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
              >
                <div className="why-icon" style={{ color: item.color }}>
                  {item.icon}
                </div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="choose-section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Why Choose Growleads LinkedIn Profile Optimizer?</h2>
          </motion.div>
          
          <div className="choose-grid">
            {[
              {
                icon: <Database size={24} />,
                title: "Built from 100+ real optimization projects",
                description: "Insights from founders, executives, agencies, creators, and job seekers."
              },
              {
                icon: <Target size={24} />,
                title: "Designed for leads, not just recruiters",
                description: "We optimize for clients, credibility and authority building."
              },
              {
                icon: <BarChart3 size={24} />,
                title: "Data-backed scoring & benchmarks",
                description: "Every score is based on actual ranking factors and keyword analysis."
              },
              {
                icon: <Sparkles size={24} />,
                title: "AI suggestions tuned for B2B & authority",
                description: "Our AI rewrites your headline and About using real frameworks."
              },
              {
                icon: <Check size={24} />,
                title: "Simple, honest pricing model",
                description: "Pay per audit. No subscriptions or contracts."
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="choose-item"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="choose-icon">
                  {item.icon}
                </div>
                <div className="choose-content">
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools by Growleads Section */}
      <section id="tools" className="tools-section">
        <div className="container">
          <motion.div 
            className="tools-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="tools-card-content">
              <div className="tools-icon-badge">
                <img 
                  src="/tools-trans.png" 
                  alt="LinkedIn Tools" 
                  className="tools-icon"
                />
              </div>
              
              <h2>More Free LinkedIn Tools</h2>
              <p>AI-powered tools for founders and professionals</p>
              
              <div className="tools-stats-row">
                <div className="stat-badge">
                  <span className="stat-num">1K+</span>
                  <span className="stat-text">Users</span>
                </div>
                <div className="stat-badge">
                  <span className="stat-num">100%</span>
                  <span className="stat-text">Free</span>
                </div>
                <div className="stat-badge">
                  <span className="stat-num">&lt;10s</span>
                  <span className="stat-text">Speed</span>
                </div>
              </div>

              <a 
                href="https://tools.growleads.io" 
                target="_blank" 
                rel="noopener noreferrer"
                className="tools-cta-btn"
              >
                <span>Try Free Tools</span>
                <ArrowRight size={18} />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="faq-section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>LinkedIn Profile Optimizer – FAQs</h2>
            <p>Everything you need to know about optimizing your LinkedIn profile</p>
          </motion.div>
          
          <div className="faq-grid">
            {[
              {
                question: "What makes a LinkedIn profile rank higher in searches?",
                answer: "LinkedIn profiles rank higher when they have relevant keywords in the headline, About section, and experience descriptions. Strong profiles also include clear positioning, industry-specific terminology, complete profile sections, active engagement history, and relevant skills endorsements. Our AI optimizer analyzes all these factors and provides specific recommendations to improve your ranking."
              },
              {
                question: "How to analyze and audit my LinkedIn profile for free?",
                answer: "Use LinkedIn Profile Optimizer to get a free comprehensive audit. Simply paste your LinkedIn profile URL, answer a few personalized questions about your goals, and our AI will analyze your profile across 50+ ranking factors. You'll receive a detailed score, keyword gap analysis, and actionable recommendations to improve your profile's visibility and effectiveness."
              },
              {
                question: "What are the best LinkedIn profile optimization practices in 2025?",
                answer: "Best practices include: writing a keyword-rich headline that clearly states what you do, creating a compelling About section with your unique value proposition, showcasing measurable achievements in your experience, using industry-specific keywords naturally, keeping your profile 100% complete, adding rich media to featured sections, and maintaining consistent activity. Our optimizer provides personalized recommendations based on these proven practices."
              },
              {
                question: "What is the best AI tool to optimize a LinkedIn profile?",
                answer: "LinkedIn Profile Optimizer by Growleads.io is designed specifically for professionals seeking better opportunities. Built from 100+ real optimization projects, our AI analyzes your profile against proven ranking factors, provides data-backed scoring, and generates personalized recommendations for headlines, About sections, and keywords. Unlike generic tools, we optimize for leads, credibility, and authority building."
              },
              {
                question: "Is LinkedIn profile optimization worth it for getting clients?",
                answer: "Yes, absolutely. An optimized LinkedIn profile can increase profile views by up to 40% and post impressions by up to 7x. Users report booking 7+ sales calls within 30 days and getting shortlisted for opportunities within one week after applying our recommendations. Your profile is often the first touchpoint with potential clients, making optimization essential for conversions."
              },
              {
                question: "How does an AI-powered LinkedIn profile optimizer work?",
                answer: "Our AI fetches your public LinkedIn profile data, then analyzes it against 50+ optimization criteria including keyword density, profile completeness, headline effectiveness, About section clarity, experience positioning, and ranking factors. It identifies gaps, compares against top-performing profiles, and generates personalized recommendations with rewritten copy examples based on your specific goals and industry."
              },
              {
                question: "How long does the LinkedIn profile audit take?",
                answer: "The complete audit process takes approximately 3-5 minutes. After you submit your LinkedIn profile URL and answer a few questions about your goals, our AI processes your profile in the background. You'll receive a comprehensive report with your profile score, detailed analysis, and actionable recommendations within minutes."
              },
              {
                question: "Will it rewrite my headline and About section?",
                answer: "Yes. Our AI generates personalized, optimized versions of your headline and About section based on your goals, industry, and target audience. The suggestions use proven frameworks from top-performing profiles while maintaining your authentic voice. You can use these recommendations as-is or adapt them to your preference."
              },
              {
                question: "Is my LinkedIn data safe?",
                answer: "Absolutely. We only access publicly available information from your LinkedIn profile URL. We don't require login credentials, passwords, or access to your LinkedIn account. Your data is processed securely and used solely for generating your personalized optimization report."
              },
              {
                question: "Can I run multiple audits?",
                answer: "Yes. You can audit your profile multiple times to track improvements after implementing our recommendations. This allows you to see how changes impact your optimization score and identify any remaining areas for enhancement."
              }
            ].map((faq, index) => (
              <motion.details
                key={index}
                className="faq-item"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
              >
                <summary>
                  <h3>{faq.question}</h3>
                  <ChevronRight className="faq-icon" size={20} />
                </summary>
                <div className="faq-answer">
                  <p>{faq.answer}</p>
                </div>
              </motion.details>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Blogs Section */}
      <LatestBlogs />

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <img 
                src="https://i.ibb.co/NgQ4T8Gx/Trans-Optimizer-Icon.png" 
                alt="LinkedIn Profile Optimizer" 
                className="footer-logo"
              />
              <h3>LinkedIn Profile Optimizer</h3>
              <p>AI-Powered LinkedIn Profile Optimizer for Founders, Consultants, Executives and Job Seekers</p>
            </div>
            
            <div className="footer-links">
              <div className="footer-column">
                <h4>Optimizer</h4>
                <a href="/">Home</a>
                <a href="#how-to-use">How to Use the LinkedIn Profile Optimizer?</a>
                <a href="#why-optimize">Why You Need to Optimize Your LinkedIn Profile?</a>
                <a href="#testimonials">Testimonials</a>
                <a href="#tools">Tools by Growleads.io</a>
                <a href="#faq">LinkedIn Profile Optimizer – FAQs</a>
              </div>
              
              <div className="footer-column">
                <h4>Resources</h4>
                <a href="https://tools.growleads.io" target="_blank" rel="noopener noreferrer">Tools by Growleads.io</a>
                <a href="https://growleads.io" target="_blank" rel="noopener noreferrer">Growleads.io</a>
                <Link to="/blog">Blog</Link>
                <a href="https://growleads.io/contact" target="_blank" rel="noopener noreferrer">Contact</a>
              </div>
              
              <div className="footer-column">
                <h4>Connect</h4>
                <a href="https://www.linkedin.com/company/linkedinprofileoptimizer/" target="_blank" rel="noopener noreferrer">LinkedIn</a>
                <a href="https://x.com/growleadsio" target="_blank" rel="noopener noreferrer">Twitter / X</a>
                <a href="https://www.instagram.com/growleads.io/" target="_blank" rel="noopener noreferrer">Instagram</a>
                <a href="https://www.facebook.com/p/GrowLeadsio-61559913546987/" target="_blank" rel="noopener noreferrer">Facebook</a>
              </div>
            </div>
          </div>
          
          <div className="footer-bottom">
            <p>
              LinkedIn Profile Optimizer All Rights Reserved 2026 © {' '}
              <a href="https://growleads.io/" target="_blank" rel="noopener noreferrer">
                Growleads.io
              </a>
              {' '} | {' '}
              <a href="https://growleads.io/terms-of-condition/" target="_blank" rel="noopener noreferrer" className="footer-link">
                Terms & Conditions
              </a>
              {' '} | {' '}
              <a href="https://growleads.io/privacy-policy/" target="_blank" rel="noopener noreferrer" className="footer-link">
                Privacy Policy
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
